/**
 * Seller service for handling seller operations
 */

import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import { extractApiErrorMessage, showUploadErrorAlert } from "../../helpers/apiError";

export interface SellerResponse {
  success: boolean;
  data: any;
  message?: string;
}

class SellerService {
  // Request cache for deduplication
  private requestCache = new Map<string, Promise<any>>();
  private cacheTimestamps = new Map<string, number>();
  private readonly CACHE_DURATION = 30 * 1000; // 30 seconds

  /**
   * Generate cache key for requests
   */
  private getCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(key: string): boolean {
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  /**
   * Clear expired cache entries
   */
  private clearExpiredCache(): void {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp >= this.CACHE_DURATION) {
        this.requestCache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }

  private isCategoryActive(category: any): boolean {
    const isActive = category?.isActive;
    const active = category?.active;
    const status = category?.status;

    if (typeof isActive === "boolean") return isActive;
    if (typeof active === "boolean") return active;
    if (typeof status === "string") return status.toLowerCase() === "active";
    return true;
  }

  private filterActiveCategoryTree(categories: any[]): any[] {
    return (categories || []).reduce<any[]>((acc, category) => {
      if (!this.isCategoryActive(category)) {
        return acc;
      }

      const children = Array.isArray(category?.children)
        ? this.filterActiveCategoryTree(category.children)
        : [];

      acc.push({
        ...category,
        children,
      });
      return acc;
    }, []);
  }

  /**
   * Create a new product as seller
   */
  async createProductAsSeller(productData: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCTS,
      productData
    );
    return response.data;
  }

  /**
   * Get all seller products with filters
   */
  async getSellerProducts(
    filters?: any,
    forceRefresh = false
  ): Promise<SellerResponse> {
    // Clear expired cache entries
    this.clearExpiredCache();

    const cacheKey = this.getCacheKey(ENDPOINTS.SELLER.PRODUCTS, filters);

    // Check if we have a valid cached request and not forcing refresh
    if (
      !forceRefresh &&
      this.requestCache.has(cacheKey) &&
      this.isCacheValid(cacheKey)
    ) {
      return this.requestCache.get(cacheKey);
    }

    // If forcing refresh, clear existing cache for this key
    if (forceRefresh) {
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    // Create new request
    const request = axiosClient
      .get<SellerResponse>(ENDPOINTS.SELLER.PRODUCTS, { params: filters })
      .then((response) => response.data);

    // Cache the request
    this.requestCache.set(cacheKey, request);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // Clear cache after request completes (success or failure)
    request.finally(() => {
      // Keep cache for the duration, don't clear immediately
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }, this.CACHE_DURATION);
    });

    return request;
  }

  /**
   * Get product by ID
   */
  async getProductById(productId: string): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCT_BY_ID(productId)
    );
    return response.data;
  }

  /**
   * Seller listing update audit history
   */
  async getProductSellerUpdateHistory(productId: string): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCT_SELLER_UPDATE_HISTORY(productId)
    );
    return response.data;
  }

  /**
   * Update a product as seller
   */
  async updateProductAsSeller(
    productId: string,
    productData: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCT_BY_ID(productId),
      productData
    );
    return response.data;
  }

  /**
   * Delete a product (soft delete)
   */
  async deleteProduct(productId: string): Promise<SellerResponse> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCT_BY_ID(productId)
    );
    return response.data;
  }

  /**
   * Save product as draft
   */
  async saveProductAsDraft(productData: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.SELLER.SAVE_DRAFT,
      productData
    );
    return response.data;
  }

  /**
   * Update product to draft status
   */
  async updateProductToDraft(
    productId: string,
    productData: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.SELLER.UPDATE_TO_DRAFT(productId),
      productData
    );
    return response.data;
  }

  /**
   * Submit product for approval
   */
  async submitProductForApproval(
    productId: string,
    productData: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.SELLER.SUBMIT_FOR_APPROVAL(productId),
      productData
    );
    return response.data;
  }

  /**
   * Get product statistics
   */
  async getProductStatistics(): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.PRODUCT_STATISTICS
    );
    return response.data;
  }

  /**
   * Perform bulk actions on products
   */
  async bulkActionOnProducts(
    productIds: string[],
    action: string,
    data?: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.SELLER.BULK_ACTIONS,
      { productIds, action, data }
    );
    return response.data;
  }

  /**
   * Duplicate a product
   */
  async duplicateProduct(productId: string): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.SELLER.DUPLICATE_PRODUCT(productId)
    );
    return response.data;
  }

  /**
   * Upload media file
   * Handles both wrapped response (TransformInterceptor) and direct { success, data } from controller.
   */
  async uploadMedia(
    file: File,
    folder?: string,
    libraryOnly = false
  ): Promise<{ id: string; url: string; thumbnailUrl?: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params: Record<string, string> = {};
      if (folder?.trim()) {
        params.folder = folder.trim();
      }
      if (libraryOnly) {
        params.libraryOnly = "true";
      }
      const response = await axiosClient.post<SellerResponse>(
        ENDPOINTS.SELLER.MEDIA.UPLOAD,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: Object.keys(params).length > 0 ? params : undefined,
          timeout: 120000,
        }
      );
      const res = response.data;
      const data = res?.data;
      const result = data?.data ?? data;
      if (result && typeof result === "object" && "url" in result) {
        return {
          id: (result as any).id ?? "",
          url: (result as any).url ?? "",
          thumbnailUrl: (result as any).thumbnailUrl,
        };
      }
      throw new Error("Upload failed: invalid response");
    } catch (error) {
      const reason = extractApiErrorMessage(
        error,
        "Media upload failed. Please try again."
      );
      showUploadErrorAlert(reason);
      throw new Error(reason);
    }
  }

  /**
   * Get media by ID
   */
  async getMedia(
    id: string
  ): Promise<{ url: string; thumbnailUrl?: string; metadata: any }> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.BY_ID(id)
    );
    const res = response.data as any;
    const outer = res?.data;
    const result = outer?.data ?? outer;
    if (result && typeof result === "object" && "url" in result) {
      return result as { url: string; thumbnailUrl?: string; metadata: any };
    }
    throw new Error("Get media failed: invalid response");
  }

  /**
   * Download media binary via API (forces save; cross-origin Spaces URLs ignore <a download>).
   */
  async downloadMediaFile(id: string, suggestedFilename: string): Promise<void> {
    const response = await axiosClient.get(ENDPOINTS.SELLER.MEDIA.DOWNLOAD(id), {
      responseType: "blob",
    });
    const blob = response.data as Blob;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = suggestedFilename;
    a.rel = "noopener";
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  /**
   * List media files with pagination
   */
  async listMedia(
    page = 1,
    limit = 20,
    search?: string,
    folder?: string,
    libraryOnly = false,
    mediaType?: "image" | "pdf"
  ): Promise<{ data: any[]; total: number; page: number; limit: number }> {
    const params: Record<string, string | number> = { page, limit };
    const q = search?.trim();
    if (q) {
      params.search = q;
    }
    const folderSlug = folder?.trim();
    if (folderSlug) {
      params.folder = folderSlug;
    }
    if (libraryOnly) {
      params.libraryOnly = "true";
    }
    if (mediaType === "image" || mediaType === "pdf") {
      params.mediaType = mediaType;
    }
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.LIST,
      { params }
    );
    // The API returns { success: true, data: { data: [], total: 0, page: 1, limit: 20 } }
    return response.data.data;
  }

  /**
   * Delete media file
   */
  async deleteMedia(id: string): Promise<void> {
    await axiosClient.delete<SellerResponse>(ENDPOINTS.SELLER.MEDIA.DELETE(id));
  }

  async deleteMultipleMedia(ids: string[]): Promise<string[]> {
    const normalizedIds = Array.from(
      new Set(
        (ids || [])
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter(Boolean)
      )
    );
    if (normalizedIds.length === 0) {
      return [];
    }

    const deleteResults = await Promise.allSettled(
      normalizedIds.map(async (id) => {
        await this.deleteMedia(id);
        return id;
      })
    );
    return deleteResults
      .filter(
        (result): result is PromiseFulfilledResult<string> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);
  }

  async listMediaFolders(
    libraryOnly = false
  ): Promise<Array<{ name: string; slug: string; count: number }>> {
    const params = libraryOnly ? { libraryOnly: "true" } : undefined;
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.FOLDERS,
      { params }
    );
    const payload = response.data?.data;
    const folders = payload?.folders ?? payload?.data?.folders ?? [];
    return Array.isArray(folders) ? folders : [];
  }

  async createMediaFolder(
    name: string
  ): Promise<{ name: string; slug: string; count: number }> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.FOLDERS,
      { name }
    );
    const payload = response.data?.data;
    const folder = payload?.data ?? payload;
    if (folder && typeof folder === "object" && "slug" in folder) {
      return folder as { name: string; slug: string; count: number };
    }
    throw new Error("Create folder failed: invalid response");
  }

  async updateMediaFolder(
    slug: string,
    name: string
  ): Promise<{ name: string; slug: string; count: number }> {
    const response = await axiosClient.patch<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.FOLDER_BY_SLUG(slug),
      { name }
    );
    const payload = response.data?.data;
    const folder = payload?.data ?? payload;
    if (folder && typeof folder === "object" && "slug" in folder) {
      return folder as { name: string; slug: string; count: number };
    }
    throw new Error("Update folder failed: invalid response");
  }

  async deleteMediaFolder(slug: string): Promise<{ movedCount: number }> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.FOLDER_BY_SLUG(slug)
    );
    const payload = response.data?.data;
    return (payload?.data ?? payload ?? { movedCount: 0 }) as {
      movedCount: number;
    };
  }

  async moveMediaToFolder(
    id: string,
    folder: string
  ): Promise<{ id: string; folder: string }> {
    const response = await axiosClient.patch<SellerResponse>(
      ENDPOINTS.SELLER.MEDIA.MOVE_TO_FOLDER(id),
      { folder }
    );
    const payload = response.data?.data;
    const result = payload?.data ?? payload;
    if (result && typeof result === "object" && "folder" in result) {
      return result as { id: string; folder: string };
    }
    throw new Error("Move media failed: invalid response");
  }

  async moveMultipleMediaToFolder(
    ids: string[],
    folder: string
  ): Promise<Array<{ id: string; folder: string }>> {
    const normalizedIds = Array.from(
      new Set(
        (ids || [])
          .map((id) => (typeof id === "string" ? id.trim() : ""))
          .filter(Boolean)
      )
    );
    if (normalizedIds.length === 0) {
      return [];
    }

    const moveResults = await Promise.allSettled(
      normalizedIds.map((id) => this.moveMediaToFolder(id, folder))
    );
    return moveResults
      .filter(
        (result): result is PromiseFulfilledResult<{ id: string; folder: string }> =>
          result.status === "fulfilled"
      )
      .map((result) => result.value);
  }

  /**
   * Get seller profile with company data
   */
  async getSellerProfile(forceRefresh = false): Promise<SellerResponse> {
    // Clear expired cache entries
    this.clearExpiredCache();

    const cacheKey = this.getCacheKey(ENDPOINTS.SELLER.PROFILE, {});

    // Check if we have a valid cached request and not forcing refresh
    if (
      !forceRefresh &&
      this.requestCache.has(cacheKey) &&
      this.isCacheValid(cacheKey)
    ) {
      return this.requestCache.get(cacheKey);
    }

    // If forcing refresh, clear existing cache for this key
    if (forceRefresh) {
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    // Create new request
    const request = axiosClient
      .get<SellerResponse>(ENDPOINTS.SELLER.PROFILE)
      .then((response) => response.data);

    // Cache the request
    this.requestCache.set(cacheKey, request);
    this.cacheTimestamps.set(cacheKey, Date.now());

    // Clear cache after request completes
    request.finally(() => {
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }, this.CACHE_DURATION);
    });

    return request;
  }

  /**
   * Update seller profile
   */
  async updateSellerProfile(profileData: any): Promise<SellerResponse> {
    console.log(
      "📤 SellerService.updateSellerProfile called with:",
      profileData
    );
    console.log("📡 Calling endpoint:", ENDPOINTS.SELLER.UPDATE_PROFILE);

    try {
      // The backend returns ProfileResponseDto directly, not wrapped
      // Use PATCH method to match backend @Patch() decorator
      const response = await axiosClient.patch<any>(
        ENDPOINTS.SELLER.UPDATE_PROFILE,
        profileData
      );
      console.log(
        "📥 SellerService.updateSellerProfile raw response:",
        response
      );
      console.log(
        "📥 SellerService.updateSellerProfile response.data:",
        response.data
      );

      // Wrap the response to match SellerResponse format
      const wrappedResponse: SellerResponse = {
        success: true,
        data: response.data,
        message: "Profile updated successfully",
      };

      // Clear profile cache on update
      const cacheKey = this.getCacheKey(ENDPOINTS.SELLER.PROFILE, {});
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);

      return wrappedResponse;
    } catch (error: any) {
      console.error("❌ SellerService.updateSellerProfile error:", error);
      // Return error response in SellerResponse format
      return {
        success: false,
        data: null,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update profile",
      };
    }
  }

  /**
   * Get all categories — always hits the network (no in-memory cache).
   */
  async getCategories(
    hierarchy = false,
    _forceRefresh = false,
    maxDepth?: number,
  ): Promise<any[]> {
    const endpoint = ENDPOINTS.CATEGORIES.BASE;
    const bust = `_t=${Date.now()}`;
    const resolvedDepth =
      hierarchy && (maxDepth !== undefined && maxDepth > 0 ? maxDepth : 2);
    const depthParam =
      hierarchy && resolvedDepth !== undefined
        ? `&maxDepth=${resolvedDepth}`
        : "";
    const url = hierarchy
      ? `${endpoint}?hierarchy=true${depthParam}&${bust}`
      : `${endpoint}?${bust}`;

    const response = await axiosClient.get<{
      success: boolean;
      data: any[];
    }>(url);
    const data = Array.isArray(response.data.data) ? response.data.data : [];
    return hierarchy
      ? this.filterActiveCategoryTree(data)
      : data.filter((category) => this.isCategoryActive(category));
  }

  // ==================== Storefront Settings ====================

  /**
   * Get storefront settings
   */
  async getStorefrontSettings(forceRefresh = false): Promise<SellerResponse> {
    this.clearExpiredCache();
    const cacheKey = this.getCacheKey(ENDPOINTS.STOREFRONT.SETTINGS, {});

    if (
      !forceRefresh &&
      this.requestCache.has(cacheKey) &&
      this.isCacheValid(cacheKey)
    ) {
      return this.requestCache.get(cacheKey);
    }

    if (forceRefresh) {
      this.requestCache.delete(cacheKey);
      this.cacheTimestamps.delete(cacheKey);
    }

    const request = axiosClient
      .get<SellerResponse>(ENDPOINTS.STOREFRONT.SETTINGS)
      .then((response) => response.data);

    this.requestCache.set(cacheKey, request);
    this.cacheTimestamps.set(cacheKey, Date.now());

    request.finally(() => {
      setTimeout(() => {
        this.requestCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }, this.CACHE_DURATION);
    });

    return request;
  }

  /**
   * Update storefront settings
   */
  async updateStorefrontSettings(settings: any): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.SETTINGS,
      settings
    );

    // Clear settings cache
    const cacheKey = this.getCacheKey(ENDPOINTS.STOREFRONT.SETTINGS, {});
    this.requestCache.delete(cacheKey);
    this.cacheTimestamps.delete(cacheKey);

    return response.data;
  }

  /**
   * Get public storefront by seller ID (for viewer side)
   */
  async getPublicStorefront(sellerId: string): Promise<any> {
    const response = await axiosClient.get(
      ENDPOINTS.STOREFRONT.PUBLIC_BY_SELLER_ID(sellerId)
    );
    // Backend returns data directly, but check if it's wrapped
    const data = response.data?.data || response.data;
    return data;
  }

  /**
   * Get paginated public storefront products by seller ID (for viewer side)
   */
  async getPublicStorefrontProducts(
    sellerId: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<{
    products: unknown[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await axiosClient.get(
      ENDPOINTS.STOREFRONT.PUBLIC_PRODUCTS_BY_SELLER_ID(sellerId),
      { params }
    );
    const data = response.data?.data || response.data;
    return data;
  }

  // ==================== Storefront Banners ====================

  /**
   * Get storefront banners
   * Returns the banners array (handles TransformInterceptor wrapper: { success, data: array }).
   */
  async getStorefrontBanners(): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.BANNERS
    );
    const res = response.data;
    const list = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : []);
    return { success: true, data: list } as SellerResponse;
  }

  /**
   * Create storefront banner
   */
  async createStorefrontBanner(banner: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.BANNERS,
      banner
    );
    return response.data;
  }

  /**
   * Update storefront banner
   */
  async updateStorefrontBanner(
    id: string,
    banner: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.BANNER_BY_ID(id),
      banner
    );
    return response.data;
  }

  /**
   * Delete storefront banner
   */
  async deleteStorefrontBanner(id: string): Promise<SellerResponse> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.STOREFRONT.BANNER_BY_ID(id)
    );
    return response.data;
  }

  // ==================== Storefront Reviews ====================

  /**
   * Get storefront reviews
   */
  async getStorefrontReviews(
    status?: "pending" | "approved" | "rejected"
  ): Promise<SellerResponse> {
    const params = status ? { status } : {};
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.REVIEWS,
      { params }
    );
    return response.data;
  }

  /**
   * Approve review
   */
  async approveReview(id: string): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.REVIEW_APPROVE(id)
    );
    return response.data;
  }

  /**
   * Reject review
   */
  async rejectReview(id: string): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.REVIEW_REJECT(id)
    );
    return response.data;
  }

  // ==================== Storefront Inquiries ====================

  /**
   * Get storefront inquiries (seller: enquiries received by my company). Paginated.
   */
  async getStorefrontInquiries(
    status?: "pending" | "responded" | "closed",
    page = 1,
    limit = 10,
    search?: string
  ): Promise<{ inquiries: any[]; total: number; page: number; limit: number }> {
    const params: Record<string, string | number> = { page, limit };
    if (status) params.status = status;
    if (search?.trim()) params.search = search.trim();
    const response = await axiosClient.get(ENDPOINTS.STOREFRONT.INQUIRIES, {
      params,
    });
    const data = response.data;
    const payload = data?.data ?? data;
    const list = Array.isArray(payload?.inquiries)
      ? payload.inquiries
      : Array.isArray(payload)
        ? payload
        : [];
    return {
      inquiries: list,
      total: Number(payload?.total ?? data?.total ?? 0) || 0,
      page: Number(payload?.page ?? data?.page ?? page) || page,
      limit: Number(payload?.limit ?? data?.limit ?? limit) || limit,
    };
  }

  /**
   * Get my enquiries (buyer: enquiries I sent, with seller response). Paginated.
   */
  async getMyStorefrontEnquiries(
    status?: "PENDING" | "RESPONDED" | "CLOSED",
    page = 1,
    limit = 10,
    search?: string
  ): Promise<{ inquiries: any[]; total: number; page: number; limit: number }> {
    const params: Record<string, string | number> = { page, limit };
    if (status) params.status = status;
    if (search?.trim()) params.search = search.trim();
    const response = await axiosClient.get(ENDPOINTS.STOREFRONT.INQUIRIES_MY, {
      params,
    });
    const data = response.data;
    const payload = data?.data ?? data;
    const list = Array.isArray(payload?.inquiries)
      ? payload.inquiries
      : Array.isArray(payload)
        ? payload
        : [];
    return {
      inquiries: list,
      total: Number(payload?.total ?? data?.total ?? 0) || 0,
      page: Number(payload?.page ?? data?.page ?? page) || page,
      limit: Number(payload?.limit ?? data?.limit ?? limit) || limit,
    };
  }

  /**
   * Create inquiry
   */
  async createInquiry(inquiry: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.INQUIRIES,
      inquiry
    );
    return response.data;
  }

  /**
   * Update inquiry status
   */
  async updateInquiryStatus(
    id: string,
    status: string,
    response?: string
  ): Promise<SellerResponse> {
    const response_data = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.INQUIRY_BY_ID(id),
      { status, response }
    );
    return response_data.data;
  }

  // ==================== Storefront Testimonials ====================

  /**
   * Get storefront testimonials
   */
  async getStorefrontTestimonials(): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.TESTIMONIALS
    );
    return response.data;
  }

  /**
   * Create testimonial
   */
  async createTestimonial(testimonial: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.TESTIMONIALS,
      testimonial
    );
    return response.data;
  }

  /**
   * Update testimonial
   */
  async updateTestimonial(
    id: string,
    testimonial: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.TESTIMONIAL_BY_ID(id),
      testimonial
    );
    return response.data;
  }

  /**
   * Delete testimonial
   */
  async deleteTestimonial(id: string): Promise<SellerResponse> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.STOREFRONT.TESTIMONIAL_BY_ID(id)
    );
    return response.data;
  }

  // ==================== Promotional Banners ====================

  /**
   * Get promotional banners
   */
  async getPromotionalBanners(): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.PROMOTIONAL_BANNERS
    );
    return response.data;
  }

  /**
   * Create promotional banner
   */
  async createPromotionalBanner(banner: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.PROMOTIONAL_BANNERS,
      banner
    );
    return response.data;
  }

  /**
   * Update promotional banner
   */
  async updatePromotionalBanner(
    id: string,
    banner: any
  ): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.PROMOTIONAL_BANNER_BY_ID(id),
      banner
    );
    return response.data;
  }

  /**
   * Delete promotional banner
   */
  async deletePromotionalBanner(id: string): Promise<SellerResponse> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.STOREFRONT.PROMOTIONAL_BANNER_BY_ID(id)
    );
    return response.data;
  }

  // ==================== Discounts ====================

  /**
   * Get discounts
   */
  async getDiscounts(): Promise<SellerResponse> {
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.DISCOUNTS
    );
    return response.data;
  }

  /**
   * Create discount
   */
  async createDiscount(discount: any): Promise<SellerResponse> {
    const response = await axiosClient.post<SellerResponse>(
      ENDPOINTS.STOREFRONT.DISCOUNTS,
      discount
    );
    return response.data;
  }

  /**
   * Update discount
   */
  async updateDiscount(id: string, discount: any): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.DISCOUNT_BY_ID(id),
      discount
    );
    return response.data;
  }

  /**
   * Delete discount
   */
  async deleteDiscount(id: string): Promise<SellerResponse> {
    const response = await axiosClient.delete<SellerResponse>(
      ENDPOINTS.STOREFRONT.DISCOUNT_BY_ID(id)
    );
    return response.data;
  }

  // ==================== Analytics ====================

  /**
   * Get storefront analytics
   */
  async getStorefrontAnalytics(
    timeRange?: "7d" | "30d" | "90d" | "1y",
    startDate?: string,
    endDate?: string
  ): Promise<SellerResponse> {
    const params: any = {};
    if (timeRange) params.timeRange = timeRange;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.ANALYTICS,
      { params }
    );
    return response.data;
  }

  // ==================== Orders ====================

  /**
   * Get storefront orders
   */
  async getStorefrontOrders(status?: string): Promise<SellerResponse> {
    const params = status ? { status } : {};
    const response = await axiosClient.get<SellerResponse>(
      ENDPOINTS.STOREFRONT.ORDERS,
      { params }
    );
    return response.data;
  }

  /**
   * Update order status
   */
  async updateOrderStatus(id: string, status: string): Promise<SellerResponse> {
    const response = await axiosClient.put<SellerResponse>(
      ENDPOINTS.STOREFRONT.ORDER_STATUS(id),
      { status }
    );
    return response.data;
  }

  /**
   * Get all sellers (Public endpoint)
   */
  async getSellers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    country?: string;
    /** Pipe-separated (|) countries; matches profile or company country */
    countries?: string;
    /** Pipe-separated (|) business type labels (same as seller profile dropdown) */
    businessTypes?: string;
    /** Pipe-separated (|) membership plan names (e.g. Gold|Silver) */
    membershipPlans?: string;
    /** Comma-separated category IDs (matches company industryTypes / subtree) */
    categoryIds?: string;
    /** Profile created in the last 90 days */
    recentlyAdded?: boolean;
    /** Recent quotes or active catalog + recent profile activity */
    highlyEngaged?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    sellers: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        sellers: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
        message: string;
        success: boolean;
      };
    }>(ENDPOINTS.SELLERS.BASE, {
      params,
    });
    return response.data.data;
  }

  /**
   * Promotion council reference directory (`promotion_councils` table) — same response shape as getSellers.
   */
  async getPromotionCouncilSellers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    countries?: string;
    councils?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    sellers: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        sellers: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
        message: string;
        success: boolean;
      };
    }>(ENDPOINTS.SELLERS.BASE, {
      params: { ...params, promotionCouncils: true },
    });
    return response.data.data;
  }

  /**
   * Distinct countries & business types for public seller directory filters
   */
  async getSellerFilterOptions(): Promise<{
    countries: string[];
    businessTypeValues: string[];
    councils: string[];
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: { countries: string[]; businessTypeValues: string[]; councils: string[] };
    }>(ENDPOINTS.SELLERS.FILTER_OPTIONS);
    return response.data.data;
  }

  /**
   * Get seller by ID (Public endpoint)
   */
  async getSellerById(id: string): Promise<{
    seller: any;
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        seller: any;
        message: string;
        success: boolean;
      };
    }>(ENDPOINTS.SELLERS.byId(id));
    return response.data.data;
  }

  /**
   * Search sellers (Public endpoint)
   */
  async searchSellers(searchParams: {
    page?: number;
    limit?: number;
    search?: string;
    country?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    sellers: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        sellers: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
        message: string;
        success: boolean;
      };
    }>(ENDPOINTS.SELLERS.SEARCH, {
      params: searchParams,
    });
    return response.data.data;
  }

  /**
   * Get top sellers (Public endpoint)
   */
  async getTopSellers(limit?: number): Promise<{
    sellers: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.get<{
      success: boolean;
      data: {
        sellers: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
        message: string;
        success: boolean;
      };
    }>(ENDPOINTS.SELLERS.TOP, {
      params: { limit },
    });
    return response.data.data;
  }
}

export const sellerService = new SellerService();
